import { Link } from 'react-router-dom';

import { useProject } from '@/hooks/use-projects';
import { ApiError } from '@/modules/api/api-client';

interface ProjectBreadcrumbProps {
  projectKey: string;
  repoName: string;
}

/**
 * Breadcrumb component for repository detail pages accessed within a project context.
 * Displays: Project Name (link to dashboard) > Repo Name (current page)
 *
 * Handles:
 * - Loading state while project data is fetched
 * - Not-found message when the project key doesn't match an existing project
 * - Not-found message when the repo doesn't belong to the specified project
 */
export function ProjectBreadcrumb({ projectKey, repoName }: ProjectBreadcrumbProps) {
  const { data: project, isLoading, isError, error } = useProject(projectKey);

  // Loading state
  if (isLoading) {
    return (
      <nav aria-label="Breadcrumb" className="mb-4">
        <ol className="flex items-center gap-2 text-sm">
          <li>
            <span className="inline-block h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </li>
          <li aria-hidden="true" className="text-gray-400 dark:text-gray-500">
            /
          </li>
          <li>
            <span className="inline-block h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </li>
        </ol>
      </nav>
    );
  }

  // Project not found (404)
  if (isError && error instanceof ApiError && error.status === 404) {
    return (
      <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20" role="alert">
        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
          Project not found
        </p>
        <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
          The project with key &ldquo;{projectKey}&rdquo; does not exist.
        </p>
        <Link
          to="/projects"
          className="mt-2 inline-block text-sm font-medium text-yellow-800 underline hover:text-yellow-900 dark:text-yellow-200 dark:hover:text-yellow-100"
        >
          Back to Projects
        </Link>
      </div>
    );
  }

  // Other errors fetching project
  if (isError) {
    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20" role="alert">
        <p className="text-sm font-medium text-red-800 dark:text-red-200">
          Failed to load project information
        </p>
        <p className="mt-1 text-sm text-red-700 dark:text-red-300">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
      </div>
    );
  }

  // Project loaded — check if repo belongs to the project
  if (project) {
    const repoBelongsToProject = project.repositories.some(
      (repo) => repo.name.toLowerCase() === repoName.toLowerCase(),
    );

    if (!repoBelongsToProject) {
      return (
        <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20" role="alert">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            Repository not found in project
          </p>
          <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
            The repository &ldquo;{repoName}&rdquo; is not associated with the project &ldquo;{project.displayName}&rdquo;.
          </p>
          <Link
            to={`/projects/${encodeURIComponent(projectKey)}`}
            className="mt-2 inline-block text-sm font-medium text-yellow-800 underline hover:text-yellow-900 dark:text-yellow-200 dark:hover:text-yellow-100"
          >
            Back to {project.displayName}
          </Link>
        </div>
      );
    }

    // Happy path: project exists and repo belongs to it
    return (
      <nav aria-label="Breadcrumb" className="mb-4">
        <ol className="flex items-center gap-2 text-sm">
          <li>
            <Link
              to={`/projects/${encodeURIComponent(projectKey)}`}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {project.displayName}
            </Link>
          </li>
          <li aria-hidden="true" className="text-gray-400 dark:text-gray-500">
            /
          </li>
          <li>
            <span className="font-medium text-gray-900 dark:text-gray-100" aria-current="page">
              {repoName}
            </span>
          </li>
        </ol>
      </nav>
    );
  }

  return null;
}
